import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export type AppLayoutMode = 'mobile' | 'tablet' | 'desktop';

const TABLET_MIN = 768;
const DESKTOP_MIN = 1024;

@Injectable({
  providedIn: 'root',
})
export class BreakpointService implements OnDestroy {
  private widthSubject = new BehaviorSubject<number>(
    typeof window !== 'undefined' ? window.innerWidth : TABLET_MIN
  );
  private onResize = () => {
    this.zone.run(() => this.widthSubject.next(window.innerWidth));
  };

  constructor(private zone: NgZone) {
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', this.onResize, { passive: true });
    this.widthSubject.next(window.innerWidth);
  }

  get width$(): Observable<number> {
    return this.widthSubject.asObservable().pipe(distinctUntilChanged());
  }

  get mode$(): Observable<AppLayoutMode> {
    return this.width$.pipe(
      map((width) => this.toMode(width)),
      distinctUntilChanged()
    );
  }

  get isMobile$(): Observable<boolean> {
    return this.mode$.pipe(map((mode) => mode === 'mobile'));
  }

  get isWide$(): Observable<boolean> {
    return this.mode$.pipe(
      map((mode) => mode !== 'mobile'),
      distinctUntilChanged()
    );
  }

  get isWide(): boolean {
    return this.toMode(this.widthSubject.value) !== 'mobile';
  }

  get isDesktop(): boolean {
    return this.toMode(this.widthSubject.value) === 'desktop';
  }

  private toMode(width: number): AppLayoutMode {
    if (width >= DESKTOP_MIN) return 'desktop';
    if (width >= TABLET_MIN) return 'tablet';
    return 'mobile';
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize);
    }
  }
}
